import React from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Shield, 
  BarChart3, 
  Smartphone, 
  DollarSign, 
  Zap,
  Globe,
  Award,
  CheckCircle,
  Clock,
  Users,
  Target
} from 'lucide-react';

// Import professional trading images
const tradingAnalysisImage = require('../assets/diverse-teammates-examining-statistics-infographics-computer_482257-90942.jpg_z2rlyq.jpg');
const professionalTradersImage = require('../assets/team-investors-analyzing-profit-amount-making-decisions_482257-88610.jpg_jptdn5.jpg');
const tradingProfitImage = require('../assets/traders-profit-354772.jpg');
const businessAnalysisImage = require('../assets/analyst-team-utilize-bi-fintech-analyze-financial-data-ornamented-meeting_31965-377645.jpg_hqqshc.jpg');

const Features = () => {
  const features = [
    {
      icon: <TrendingUp className="h-8 w-8 text-emerald-600" />,
      title: 'Real-Time Market Data',
      description: 'Access live market feeds with millisecond precision. Get real-time quotes, charts, and market depth for informed trading decisions.',
      color: 'emerald',
      benefits: ['Live price feeds', 'Market depth data', 'Real-time news', 'Economic calendar']
    },
    {
      icon: <Shield className="h-8 w-8 text-blue-600" />,
      title: 'Bank-Grade Security',
      description: 'Your funds and personal data are protected with military-grade encryption and multi-layer security protocols.',
      color: 'blue',
      benefits: ['256-bit encryption', '2FA authentication', 'Cold storage', 'Insurance coverage']
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-purple-600" />,
      title: 'Advanced Analytics',
      description: 'Professional-grade charting tools with 100+ technical indicators, drawing tools, and automated analysis.',
      color: 'purple',
      benefits: ['100+ indicators', 'Custom alerts', 'Pattern recognition', 'Backtesting']
    },
    {
      icon: <DollarSign className="h-8 w-8 text-orange-600" />,
      title: 'Zero Commission Trading',
      description: 'Trade stocks and ETFs with zero commission fees. Keep more of your profits with our competitive pricing.',
      color: 'orange',
      benefits: ['$0 stock trades', 'No account minimums', 'Competitive spreads', 'Transparent pricing']
    },
    {
      icon: <Smartphone className="h-8 w-8 text-green-600" />,
      title: 'Mobile Trading',
      description: 'Trade on the go with our award-winning mobile app. Full platform functionality in your pocket.',
      color: 'green',
      benefits: ['iOS & Android apps', 'Touch ID login', 'Push notifications', 'Offline charts']
    },
    {
      icon: <Zap className="h-8 w-8 text-indigo-600" />,
      title: 'Lightning Execution',
      description: 'Ultra-fast order execution with sub-millisecond latency. Never miss a trading opportunity again.',
      color: 'indigo',
      benefits: ['Sub-ms execution', 'Smart routing', 'No slippage', '99.9% uptime']
    }
  ];

  const tradingStats = [
    { number: '1M+', label: 'Active Traders', icon: <Users className="h-6 w-6" /> },
    { number: '$50B+', label: 'Daily Volume', icon: <TrendingUp className="h-6 w-6" /> },
    { number: '0.001s', label: 'Avg Execution', icon: <Zap className="h-6 w-6" /> },
    { number: '150+', label: 'Global Markets', icon: <Globe className="h-6 w-6" /> }
  ];

  return (
    <section id="features" className="features-section">
      <div className="features-container">
        
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="features-header"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="features-badge"
          >
            <Target className="features-badge-icon" />
            Professional Trading Platform
          </motion.div>
          
          <h2 className="features-title">
            <span className="features-title-primary">Why Choose</span>
            <br />
            <span className="features-title-gradient">ChartLine Brokerage</span>
          </h2>
          
          <p className="features-subtitle">
            Experience the power of professional trading with cutting-edge technology, 
            institutional-grade security, and award-winning customer support.
          </p>
        </motion.div>

        {/* Hero Image Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative mb-20"
        >
          <div className="relative h-96 lg:h-[500px] rounded-3xl overflow-hidden shadow-2xl">
            <img
              src={tradingAnalysisImage}
              alt="Professional Trading Analysis"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/40 to-emerald-900/60" />
            
            {/* Overlay Content */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white max-w-3xl px-6">
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="text-3xl lg:text-4xl font-bold mb-4"
                >
                  Professional Trading, Simplified
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="text-lg lg:text-xl opacity-90"
                >
                  Join millions of traders who trust ChartLine for their investment journey
                </motion.p>
              </div>
            </div>

            {/* Floating Stats */}
            <div className="absolute bottom-6 left-6 right-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {tradingStats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20"
                  >
                    <div className="flex items-center justify-center mb-2 text-emerald-400">
                      {stat.icon}
                    </div>
                    <div className="text-xl font-bold text-white">{stat.number}</div>
                    <div className="text-sm text-white/80">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8 }}
              className="features-card group"
            >
              <div className="features-card-glow"></div>
              
              <div className="features-card-content">
                {/* Icon */}
                <div className={`features-card-icon ${feature.color}`}>
                  {feature.icon}
                </div>
                
                {/* Content */}
                <h3 className="features-card-title">
                  {feature.title}
                </h3>
                
                <p className="features-card-description mb-6">
                  {feature.description}
                </p>

                {/* Benefits List */}
                <div className="space-y-2">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{benefit}</span>
                    </div>
                  ))}
                </div>
                
                {/* Hover Effect Border */}
                <div className="features-card-border"></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Features Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left: Image */}
            <div className="relative">
              <div className="relative h-80 lg:h-96 rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={professionalTradersImage}
                  alt="Professional Trading Team"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                
                {/* Badge */}
                <div className="absolute top-6 left-6">
                  <div className="bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    Award Winning Platform
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="space-y-6">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-full font-medium text-sm mb-4">
                  <Award className="h-4 w-4" />
                  Industry Recognition
                </div>
                <h3 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  Trusted by <span className="text-emerald-600">Professionals</span>
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Our platform has been recognized by industry leaders and trusted by professional 
                  traders worldwide. Experience the difference with our institutional-grade technology.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="text-2xl font-bold text-slate-900">99.9%</div>
                  <div className="text-sm text-slate-600">Platform Uptime</div>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <div className="text-2xl font-bold text-slate-900">24/7</div>
                  <div className="text-sm text-slate-600">Expert Support</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  Start Trading Now
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 border border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all duration-300"
                >
                  Learn More
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="features-cta"
        >
          <div className="bg-gradient-to-r from-emerald-600 to-blue-600 rounded-3xl p-8 lg:p-12 text-center text-white">
            <h3 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Start Trading?
            </h3>
            <p className="text-lg lg:text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Join over 1 million traders who trust ChartLine for their trading success. 
              Open your account today and start trading with zero commissions.
            </p>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-white text-emerald-600 font-bold rounded-xl hover:bg-slate-50 transition-all duration-300"
            >
              Open Trading Account
            </motion.button>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Features; 